import { useMemo, useState } from 'react';
import {
  fieldColumnId,
  getAdvancedReportColumnDefs
} from './advanced-reports-column-defs';
import {
  ColumnResizeMode,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { selectOpenRowIds } from './advanced-reports-table.slice';
import { DrillthroughTable } from 'common/components/table/drillthrough-table/drillthrough-table';
import { DrillthroughTableInjectableRow } from 'common/components/table/drillthrough-table/drillthrough-table.types';
import {
  createRows,
  createTotalRow
} from './factories/advanced-report-table-row-factory';
import { createDrillthroughTableInjectableRow } from './factories/advanced-report-drillthrough-table-injectable-row-factory';
import Tile from 'features/tiles/tile';
import { AdvancedReportExport } from './advanced-reports-table.types';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { getSelectedAttributionModel } from 'features/attribute-model/availableAttributionModelsSlice';
import AdvancedReportCsvExportButton from './export/advanced-report-csv-export-button';
import { sortRows } from './factories/advanced-report-table-helpers';
import { useGetAdvancedReportResults } from './hooks/use-get-advanced-report-results';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { areSomeRequestsStillPending } from '../helpers/query-helpers';
import { RootState } from 'app/store';
import { selectIsAttributionComparisonActive } from 'features/attribute-compare-model/availableAttributionCompareModelsSlice';
import {
  selectHasSegmentFilters,
  selectIsDirty
} from 'features/segment-controls/segment-controls.slice';

interface AdvancedReportsTableProps {
  componentId: string;
}

const AdvancedReportsTable = (props: AdvancedReportsTableProps) => {
  const { componentId } = props;
  const currencyCode = useSelector(selectCurrencyCode);
  const openRowIds = useSelector(selectOpenRowIds);
  const isDirty = useSelector((state: RootState) =>
    selectIsDirty(state, componentId)
  );

  const { startDate, endDate } = useSelector(getDateRangeAsObjects);

  const { isPocModeActive } = useSelector(
    (state: RootState) => state.pocToggle
  );
  const attributionModel = useSelector((state: RootState) =>
    getSelectedAttributionModel(state, isPocModeActive)
  );

  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const [sorting, setSorting] = useState<SortingState>([]);

  const displayComparisonAttributionModelData = useSelector(
    (state: RootState) => selectIsAttributionComparisonActive(state)
  );

  const doesAdvancedReportHaveAFilter = useSelector((state: RootState) =>
    selectHasSegmentFilters(state, componentId)
  );

  const {
    primaryRowsResult,
    primaryQueryStatus,
    compareRowsResult,
    compareQueryStatus
  } = useGetAdvancedReportResults(displayComparisonAttributionModelData);

  const metricRows = useMemo(() => {
    const metricRows = createRows(primaryRowsResult, compareRowsResult);

    const sortedRows =
      sorting.length > 0
        ? sortRows(metricRows, sorting[0].id, sorting[0].desc)
        : sortRows(metricRows, fieldColumnId, false);

    return sortedRows;
  }, [primaryRowsResult, compareRowsResult, sorting]);

  const columnDefs = getAdvancedReportColumnDefs(
    currencyCode,
    primaryQueryStatus,
    compareQueryStatus,
    displayComparisonAttributionModelData
  );

  const areQueriesFinished =
    !areSomeRequestsStillPending(primaryQueryStatus) &&
    !areSomeRequestsStillPending(compareQueryStatus);

  const exportRequest = useMemo(() => {
    const reportExport: AdvancedReportExport = {
      orderedColumnIds: columnDefs.map((column) => column.id!),
      rows: metricRows,
      startDate,
      endDate,
      currencyCode,
      attributionModel
    };
    return reportExport;
  }, [
    columnDefs,
    metricRows,
    startDate,
    endDate,
    currencyCode,
    attributionModel
  ]);

  const rootRows = useMemo(
    () => metricRows.filter((row) => row.parentId === ''),
    [metricRows]
  );

  const drillthroughRows = useMemo(() => {
    const rows = metricRows
      .filter((row) => openRowIds.includes(row.parentId))
      .filter(
        (row) => !(doesAdvancedReportHaveAFilter && row.field === '(not set)')
      )
      .map<DrillthroughTableInjectableRow>((row) =>
        createDrillthroughTableInjectableRow(
          row,
          currencyCode,
          columnDefs.map((column) => column.id!),
          displayComparisonAttributionModelData
        )
      );

    return rows;
  }, [
    metricRows,
    openRowIds,
    doesAdvancedReportHaveAFilter,
    currencyCode,
    columnDefs,
    displayComparisonAttributionModelData
  ]);

  const totalRow = useMemo(() => {
    const totalRow = createTotalRow(rootRows);
    return createDrillthroughTableInjectableRow(
      totalRow,
      currencyCode,
      columnDefs.map((column) => column.id!),
      displayComparisonAttributionModelData
    );
  }, [
    rootRows,
    currencyCode,
    columnDefs,
    displayComparisonAttributionModelData
  ]);

  const advancedReportsTable = useReactTable({
    data: rootRows,
    columns: columnDefs,
    manualSorting: true,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
    columnResizeMode
  });

  const hasQueryRun =
    primaryQueryStatus.impressions !== QueryStatus.uninitialized &&
    primaryQueryStatus.clicks !== QueryStatus.uninitialized &&
    primaryQueryStatus.calls !== QueryStatus.uninitialized &&
    primaryQueryStatus.webs !== QueryStatus.uninitialized &&
    primaryQueryStatus.opps !== QueryStatus.uninitialized &&
    primaryQueryStatus.oppValues !== QueryStatus.uninitialized &&
    primaryQueryStatus.sales !== QueryStatus.uninitialized &&
    primaryQueryStatus.revenues !== QueryStatus.uninitialized &&
    primaryQueryStatus.costs !== QueryStatus.uninitialized;

  return (
    <div>
      {metricRows?.length > 0 ? (
        <>
          <div className="flex justify-end mb-4">
            <AdvancedReportCsvExportButton
              exportRequest={exportRequest}
              isDisabled={!areQueriesFinished}
            />
          </div>
          <Tile>
            {isDirty && hasQueryRun ? (
              <div className="flex items-center mb-2.5 border border-warning-600 bg-warning-100 px-2 py-1">
                <i className="ra-icons-generic-warning-2 text-xl text-warning-600 flex"></i>
                <div className="ml-1.5 flex flex-col">
                  <p className="text-xs">
                    These table results no longer reflect the segment controls.
                    Re-run the query to see the results.
                  </p>
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className={`${isDirty && hasQueryRun ? 'opacity-60' : ''}`}>
              <DrillthroughTable
                key={`advanced-reports-table`}
                componentId={'advanced-reports-table'}
                heightClass="max-h-2/3-screen"
                table={advancedReportsTable}
                totalRow={totalRow}
                injectableRows={drillthroughRows}
              />
            </div>
          </Tile>
        </>
      ) : (
        <>
          {isDirty && hasQueryRun && areQueriesFinished ? (
            <div className="flex items-center mb-2.5 border border-warning-600 bg-warning-100 px-2 py-1">
              <i className="ra-icons-generic-warning-2 text-xl text-warning-600 flex"></i>
              <div className="ml-1.5 flex flex-col">
                <p className="text-xs">
                  No data found for the selected segments, date range and
                  filters.
                </p>
                <p className="text-xs">
                  If you are unable to retrieve any data for this segment.
                  Please{' '}
                  <a
                    className="underline text-secondary-600 hover:text-tertiary-600"
                    href="https://www.ruleranalytics.com/contact/"
                  >
                    contact support
                  </a>{' '}
                  to discuss further.
                </p>
              </div>
            </div>
          ) : (
            <></>
          )}
        </>
      )}
    </div>
  );
};

export default AdvancedReportsTable;
